<template>
  <article class="product-ads-list-item">
    <div class="product-ads-list-item__info">
      <EHeading
        as="h3"
        class="product-ads-list-item__title"
        data-test-id="product-ads-list-item-title"
      >
        <ELink
          :href="url"
          :target="linkTarget"
          :title="title"
          color="result"
          class="product-ads-list-item__link"
          data-test-id="product-ads-list-link"
          @click="onLinkClick"
          @auxclick="onLinkClick"
        >
          {{ title }}
        </ELink>
      </EHeading>
      <div v-if="formattedPrice" class="product-ads-list-item__details">
        <span
          class="product-ads-list-item__price"
          data-test-id="product-ads-list-price"
        >
          {{ formattedPrice }}
        </span>
        <template v-if="extraInfo.length > 0">
          <span
            v-for="(info, index) in extraInfo"
            :key="index"
            class="product-ads-list-item__extra"
          >
            <span v-if="index > 0" aria-hidden="true">•</span>
            {{ info }}
          </span>
        </template>
      </div>
      <div class="product-ads-list-item__source" data-test-id="product-ads-list-item-source">
        <EIcon
          v-if="badge"
          icon="check-circle"
          size="s"
          class="product-ads-list-item__badge"
          data-track-id="pa-merchant-badge"
        />
        {{ source }}
      </div>
    </div>
    <div class="product-ads-list-item__thumbnail">
      <img
        v-lazy="adjustedThumbnailUrl"
        class="product-ads-list-item__image"
        role="presentation"
        @error="error = true"
      >
    </div>
  </article>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { privacyProxy, privacyProxyMs } from '~~/common/js/constants/feature-flags';
import { formatPrice } from '~~/common/js/universal/intl';
import EHeading from '~~/common/vue/components/heading';
import ELink from '~~/common/vue/components/link';
import EIcon from '~~/common/vue/components/icon';
import { trackImpression, trackClick, TYPE_PRODUCT_AD } from '~~/search/common/js/client/analytics/results';
import { adjustThumbnailSize, getProxiedUrl } from '~~/search/common/js/universal/urls';

const THUMBNAIL_HEIGHT = 50;

export default {
  name: 'EProductAdsListItem',
  components: {
    EHeading,
    ELink,
    EIcon,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: '',
    },
    thumbnailUrl: {
      type: String,
      default: '',
    },
    price: {
      type: Object,
      default: null,
    },
    shippingInfo: {
      type: String,
      default: '',
    },
    pricePerUnit: {
      type: String,
      default: '',
    },
    energyRating: {
      type: String,
      default: '',
    },
    badge: {
      type: Boolean,
      default: false,
    },
    pingSuffix: {
      type: String,
      default: '',
    },
    rank: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    error: false,
  }),
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('user', ['linkTarget']),
    ...mapGetters('unleash', ['getFeatureToggle']),
    shouldProxy() {
      return this.getFeatureToggle(privacyProxy.flag)?.value === privacyProxy.enabled;
    },
    adjustedThumbnailUrl() {
      const url = (!this.shouldProxy || this.error) ?
        this.thumbnailUrl : getProxiedUrl(this.thumbnailUrl, this.$config.proxyHost);

      return adjustThumbnailSize(url, { height: THUMBNAIL_HEIGHT });
    },
    formattedPrice() {
      if (!this.price) {
        return null;
      }

      const { value, currency } = this.price;
      return formatPrice(this.locale, currency, value);
    },
    extraInfo() {
      return [this.shippingInfo, this.pricePerUnit, this.energyRating]
        .filter((info) => !!info);
    },
    trackingOptions() {
      return {
        extensions: this.badge ? [{ type: 'merchantBadge' }] : [],
        position: 'sidebar',
        rank: this.rank,
        title: this.title,
        type: TYPE_PRODUCT_AD,
        url: this.source,
      };
    },
    shouldProxyTracking() {
      return this.getFeatureToggle(privacyProxyMs.flag)?.value === privacyProxyMs.enabled;
    },
  },
  mounted() {
    trackImpression(this.trackingOptions);
  },
  methods: {
    ...mapActions(['trackAdClick']),
    onLinkClick() {
      // Ecosia in-house tracking
      trackClick(this.trackingOptions);
      // TODO: rename action to clarify this is Bing tracking
      this.trackAdClick(this.pingSuffix, this.shouldProxyTracking);
    },
  },
};
</script>

<style lang="scss" scoped>
.product-ads-list-item {
  display: flex;
}

.product-ads-list-item__info {
  flex: 1 0 0%;
  min-width: 0;
  margin-right: $space-s;
}

.product-ads-list-item__title {
  @extend %ellipsis;

  font-size: $font-m;
  font-weight: $font-weight-m;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.product-ads-list-item__image {
  max-height: $space-4l;
  max-width: $space-4l;
}

.product-ads-list-item__details {
  align-items: baseline;
  display: flex;
  margin-top: $space-2s;
}

.product-ads-list-item__price,
.product-ads-list-item__extra {
  @extend %ellipsis;
}

.product-ads-list-item__price {
  flex-shrink: 0;
  font-weight: $font-weight-1l;
}

.product-ads-list-item__extra {
  color: var(--color-text-secondary);
  font-size: $font-s;
  font-weight: $font-weight-m;
  margin-left: $space-2s;
}

.product-ads-list-item__source {
  @extend %ellipsis;

  color: var(--color-url);
}

.product-ads-list-item__badge {
  color: var(--color-decorative-icon);
}
</style>
